import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, useScroll, useMotionValueEvent, useSpring, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// Magnetic Button Component
function MagneticButton({
  children,
  className,
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouse = (e: React.MouseEvent<HTMLButtonElement>) => {
    const {
      clientX,
      clientY
    } = e;
    const {
      height,
      width,
      left,
      top
    } = ref.current!.getBoundingClientRect();
    const middleX = clientX - (left + width / 2);
    const middleY = clientY - (top + height / 2);
    setPosition({
      x: middleX * 0.2,
      y: middleY * 0.2
    });
  };
  const reset = () => {
    setPosition({
      x: 0,
      y: 0
    });
  };
  const {
    x,
    y
  } = position;
  return <motion.button ref={ref} onMouseMove={handleMouse} onMouseLeave={reset} onClick={onClick} animate={{
    x,
    y
  }} transition={{
    type: 'spring',
    stiffness: 150,
    damping: 15,
    mass: 0.1
  }} className={className} data-id="element-202">
      {children}
    </motion.button>;
}
export function Navbar({
  onOpenNewsletter
}: {
  onOpenNewsletter?: () => void;
}) {
  const {
    scrollY,
    scrollYProgress
  } = useScroll();
  const location = useLocation();
  const navigate = useNavigate();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  // Force solid background on non-home pages
  const isHomePage = location.pathname === '/';
  useMotionValueEvent(scrollY, 'change', latest => {
    if (latest > 50) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  });
  const navLinks = [{
    name: 'Company',
    href: '/company/about',
    subLinks: [{
      name: 'About',
      href: '/company/about'
    }, {
      name: 'Board',
      href: '/company/board'
    }, {
      name: 'Team',
      href: '/company/team'
    }, {
      name: 'Partners',
      href: '/company/partners'
    }, {
      name: 'Careers & Culture',
      href: '/company/careers'
    }, {
      name: 'Contact Us',
      href: '/company/contact'
    }]
  }, {
    name: 'Our Concepts',
    href: '/concepts/restaurants',
    subLinks: [{
      name: 'Restaurants',
      href: '/concepts/restaurants'
    }, {
      name: 'Private Dining & Events',
      href: '/concepts/private-dining'
    }, {
      name: 'Grab & Go',
      href: '/concepts/grab-and-go'
    }, {
      name: 'Commissary & Catering',
      href: '/concepts/catering'
    }, {
      name: 'Ready-to-Cook',
      href: '/concepts/ready-to-cook'
    }]
  }, {
    name: 'Advisory & Investment',
    href: '/advisory',
    subLinks: [{
      name: 'Overview',
      href: '/advisory'
    }, {
      name: 'Investment & Growth',
      href: '/advisory/investment'
    }, {
      name: 'Real Estate',
      href: '/advisory/real-estate'
    }, {
      name: 'Management Company',
      href: '/advisory/management'
    }]
  }, {
    name: 'Memberships',
    href: '/memberships',
    subLinks: [{
      name: 'Overview',
      href: '/memberships'
    }, {
      name: 'Personal',
      href: '/memberships/personal'
    }, {
      name: 'Corporate',
      href: '/memberships/corporate'
    }]
  }, {
    name: 'Press / Media',
    href: '/press'
  }];
  const handleDropdownEnter = (name: string) => {
    setActiveDropdown(name);
  };
  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };
  const navBackground = isScrolled || !isHomePage || activeDropdown ? 'bg-brand-dark/95 backdrop-blur-md shadow-lg py-4 border-b border-white/5' : 'bg-transparent py-6';
  return <>
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-brand-gold z-[60] origin-left" style={{
      scaleX
    }} data-id="element-203" />
      <motion.nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBackground}`} initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} onMouseLeave={handleDropdownLeave} data-id="element-204">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-205">
          <Link to="/" className="flex items-center gap-3 group z-50" data-id="element-206">
            <img src="https://cdn.magicpatterns.com/uploads/cPkLq24FKKtQpiCoiqri7q/Ресурс_1.svg" alt="Jibek Jolu Group Logo" className={`transition-all duration-300 ${isScrolled || !isHomePage ? 'h-10' : 'h-12'} filter invert`} data-id="element-207" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8 h-full" data-id="element-208">
            {navLinks.map(link => <div key={link.name} className="relative py-4" onMouseEnter={() => handleDropdownEnter(link.name)} data-id="element-209">
                <Link to={link.href} className="text-white/80 hover:text-brand-gold text-sm font-medium tracking-wide transition-colors flex items-center gap-1" data-id="element-210">
                  {link.name}
                  {link.subLinks && <ChevronDown size={14} className={`transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''}`} data-id="element-211" />}
                </Link>

                {/* Dropdown Menu */}
                {link.subLinks && <AnimatePresence data-id="element-212">
                    {activeDropdown === link.name && <motion.div initial={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 10,
                scale: 0.95
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="absolute top-full left-0 mt-2 w-56 bg-brand-dark border border-white/10 rounded-sm shadow-2xl overflow-hidden" data-id="element-213">
                        <div className="py-2 flex flex-col" data-id="element-214">
                          {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.href} className="px-4 py-2.5 text-sm text-white/70 hover:text-brand-gold hover:bg-white/5 transition-colors" onClick={() => setActiveDropdown(null)} data-id="element-215">
                              {subLink.name}
                            </Link>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>}
              </div>)}

            <button onClick={onOpenNewsletter} className="text-white/80 hover:text-brand-gold text-sm font-medium tracking-wide transition-colors" data-id="element-216">
              Newsletter
            </button>
          </div>

          <div className="hidden lg:block z-50" data-id="element-217">
            <MagneticButton className="bg-brand-gold hover:bg-brand-gold/90 text-brand-dark px-8 py-3 rounded-sm font-medium text-sm transition-colors shadow-lg shadow-brand-gold/20" onClick={() => navigate('/company/contact')} data-id="element-218">
              Reservations
            </MagneticButton>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden text-white p-2 z-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-219">
            {isMobileMenuOpen ? <X size={24} data-id="element-220" /> : <Menu size={24} data-id="element-221" />}
          </button>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence data-id="element-222">
          {isMobileMenuOpen && <motion.div className="lg:hidden absolute top-full left-0 right-0 bg-brand-dark border-t border-white/10 shadow-xl max-h-[80vh] overflow-y-auto" initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} data-id="element-223">
              <div className="flex flex-col p-6 gap-6" data-id="element-224">
                {navLinks.map(link => <div key={link.name} className="flex flex-col gap-3" data-id="element-225">
                    <Link to={link.href} className="text-white hover:text-brand-gold text-lg font-serif border-b border-white/10 pb-2" onClick={() => !link.subLinks && setIsMobileMenuOpen(false)} data-id="element-226">
                      {link.name}
                    </Link>
                    {link.subLinks && <div className="flex flex-col gap-2 pl-4 border-l border-white/10" data-id="element-227">
                        {link.subLinks.map(subLink => <Link key={subLink.name} to={subLink.href} className="text-white/60 hover:text-brand-gold text-sm py-1" onClick={() => setIsMobileMenuOpen(false)} data-id="element-228">
                            {subLink.name}
                          </Link>)}
                      </div>}
                  </div>)}

                <button onClick={() => {
              setIsMobileMenuOpen(false);
              if (onOpenNewsletter) onOpenNewsletter();
            }} className="text-left text-white hover:text-brand-gold text-lg font-serif border-b border-white/10 pb-2" data-id="element-229">
                  Newsletter
                </button>

                <Link to="/company/contact" className="bg-brand-gold text-brand-dark px-6 py-3 mt-4 rounded-sm font-medium text-sm w-full block text-center" onClick={() => setIsMobileMenuOpen(false)} data-id="element-230">
                  Reservations
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>
    </>;
}